<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            title="Billing Address"
        />

        <div class="-mx-4 mt-2 h-4 bg-gray-50 sm:hidden" />

        <recurring-information
            v-if="requiresRecurringToken"
            ref="recurringInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
        />

        <document-section
            :contract="contract"
            :is-signing-document="
                isSubmittingInlineContract || isValidatingRecurring
            "
            balance-due-mode
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 mt-4 font-medium"
        >
            Please fix the errors in red above.
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type {
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, ref, toRef } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { GraphQLRequestError } from 'awesome-graphql-client'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { useKioskSuccess } from '~/composables/use-kiosk-success'
import { useShowAlert } from '#imports'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract?: FragmentType<typeof DocumentSectionContractFragment>
}>()

const {
    checkoutData,

    primaryCustomer,
    facility,

    fullAddressRequired,

    editingBillingAddress,

    recurringTotal,
    requiresRecurringToken
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        address: BillingAddressSchema,
        recurring: RecurringInformationSchema
    })
)

const { handleSubmit, errors, validateField } = useForm({
    validationSchema: schema,
    initialValues: {
        address: BillingAddressInitialValues(
            primaryCustomer.value,
            fullAddressRequired.value
        ),
        recurring: RecurringInformationInitialValues(
            checkoutData.value.recurringTotal
        )
    }
})

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(() => pendingRecurringToken.value === false)
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const isValidatingRecurring = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)

async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    isValidatingRecurring.value = true
    // If recurring field is not valid, submit stripe form
    // Use mode: silent so we don't flash, the error message
    const { valid: validRecurring } = await validateField('recurring', {
        mode: 'silent'
    })
    if (!validRecurring) {
        await recurringInformationRef.value.submitStripeForm()
    }

    isValidatingRecurring.value = false

    return onSubmit(event)
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const query = useRpQuery()
const { mutate: inlineSubmitContract, isPending: isSubmittingInlineContract } =
    useMutation({
        mutationFn: async (mutationData: CommitCheckoutInput) => {
            const response = await query(
                graphql(/** GraphQL */ `
                    mutation InlineSubmitContract(
                        $input: InlineSubmitContractInput!
                    ) {
                        inlineSubmitContract(input: $input) {
                            __typename
                            ... on InlineSubmitContractResult {
                                contract {
                                    __typename
                                    ... on PlanContract {
                                        referenceNumber
                                        planType
                                        customer {
                                            firstName
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    path
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData
                }
            )

            if (response.inlineSubmitContract === null) {
                throw new Error(
                    'inlineSubmitContract guest null return. Please try again'
                )
            }

            if (
                response.inlineSubmitContract.__typename ===
                    'InlineSubmitContractResult' ||
                response.inlineSubmitContract.__typename === 'ValidationError'
            ) {
                return response.inlineSubmitContract
            }

            throw new Error()
        }
    })

const kioskSuccessFunction = useKioskSuccess()

const showAlert = useShowAlert()
const savedDocumentInput = ref()
const onSubmit = handleSubmit(values => {
    // Customer and Address
    const customer = {
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Recurring Token
    const recurring = requiresRecurringToken.value
        ? {
              ...values.recurring
          }
        : null

    inlineSubmitContract(
        {
            checkoutId: checkoutData.value.id,
            customer,
            recurring,
            document: savedDocumentInput.value
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert('Heads Up', messageBuilder)
                    }
                } else {
                    kioskSuccessFunction({
                        includedInvoice: false,
                        wasGuest: false,
                        data: {
                            referenceNumber: data.contract.referenceNumber,
                            planType: data.contract.planType,
                            firstName: data.contract.customer.firstName
                        }
                    })
                }
            },
            onError(error, variables, context) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert('Heads Up', String(error))
                }
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
    handleSubmitCheckoutForm().catch(error => {
        showAlert('Heads Up', String(error))
    })
}
</script>
